import axios, { AxiosError } from 'axios';

// Use relative path for Vite proxy, or full URL for production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL 
  ? '' // Use relative path when VITE_API_BASE_URL is set (Vite proxy handles it)
  : (import.meta.env.VITE_API_URL || '');

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// Request interceptor - add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token expiration
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<any>) => {
    // Handle token expiration or invalid token
    // Redirect to login on ANY 401 — regardless of error code.
    // Specific error codes are kept for reference but 401 always means auth failure.
    if (error.response?.status === 401) {
      console.log('🔒 Authentication error (401) detected, redirecting to login...');
      // Clear auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('customerData');
      localStorage.removeItem('businessData');
      localStorage.removeItem('user');
      
      // Redirect to login
      window.location.href = '/auth/login';
      return Promise.reject(error); // Stop further processing
    }

    if (error.response?.status === 403) {
      const errorCode = error.response?.data?.code;
      // Only redirect on explicit auth-related 403 codes (not business permission errors)
      if (
        errorCode === 'INVALID_TOKEN' || 
        errorCode === 'MISSING_TOKEN' || 
        errorCode === 'JWT_AUTH_ERROR' || 
        errorCode === 'AUTH_FAILED' ||
        errorCode === 'INVALID_TOKEN_FORMAT'
      ) {
        console.log('🔒 Auth 403 error detected, redirecting to login...');
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('customerData');
        localStorage.removeItem('businessData');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get current business ID from localStorage
 * Returns the business ID or null if not found
 */
export function getBusinessId(): string | null {
  try {
    // Try to get from businessData first (most reliable)
    const businessData = localStorage.getItem('businessData');
    if (businessData) {
      const parsed = JSON.parse(businessData);
      if (parsed.id) {
        return parsed.id;
      }
    }
    
    // Fallback 1: try user data (added during login)
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      if (parsed.business_id) {
        return parsed.business_id;
      }
    }
    
    // Fallback 2: try customerData
    const customerData = localStorage.getItem('customerData');
    if (customerData) {
      const parsed = JSON.parse(customerData);
      if (parsed.business_id) {
        return parsed.business_id;
      }
      // Check if businesses array exists
      if (parsed.businesses && parsed.businesses.length > 0) {
        return parsed.businesses[0].id;
      }
    }
    
    // Fallback 3: decode from JWT token
    const token = localStorage.getItem('authToken') || localStorage.getItem('accessToken');
    if (token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      if (decoded.business_id || decoded.businessId) {
        return decoded.business_id || decoded.businessId;
      }
    }
  } catch (error) {
    console.error('Failed to get business ID:', error);
  }
  
  console.warn('Business ID not found in any storage location. User may need to login again.');
  return null;
}

export default axiosInstance;
export { API_BASE_URL };
